import { useState } from 'react';
import { formatDateTime, describeUserAgent } from '@shared/format';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, PageHeader } from '../components/ui';
import { Table, Pagination, type Column } from '../components/table';

interface HistoryItem {
  id: string;
  at: string;
  email: string;
  result: 'success' | 'failure' | 'locked';
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
}

interface HistoryResponse {
  items: HistoryItem[];
  page: number;
  pageSize: number;
  total: number;
}

const RESULT_LABEL: Record<HistoryItem['result'], { text: string; tone: 'ok' | 'danger' | 'warn' }> = {
  success: { text: '成功', tone: 'ok' },
  failure: { text: '失败', tone: 'danger' },
  locked: { text: '已锁定', tone: 'warn' },
};

/**
 * 登录历史。
 *
 * 失败原因如实展示（口令错误第几次、是否被锁定），因为这一页的用途就是
 * **让账号本人发现有人在试自己的口令**。展示自己的数据不构成信息泄漏。
 */
export function LoginHistoryPage() {
  const [page, setPage] = useState(1);
  const history = useAsync<HistoryResponse>(
    () => api.get(`/api/security/login-history?page=${page}&pageSize=20`),
    [page],
  );

  const columns: Column<HistoryItem>[] = [
    {
      key: 'at',
      header: '时间',
      render: (row) => <span className="tabular text-fg">{formatDateTime(row.at)}</span>,
    },
    {
      key: 'result',
      header: '结果',
      render: (row) => <Badge tone={RESULT_LABEL[row.result].tone}>{RESULT_LABEL[row.result].text}</Badge>,
    },
    {
      key: 'reason',
      header: '说明',
      render: (row) => <span className="text-fg-muted">{row.reason ?? '—'}</span>,
    },
    {
      key: 'ip',
      header: 'IP',
      render: (row) => <span className="font-mono text-xs text-fg-muted">{row.ip ?? '—'}</span>,
    },
    {
      key: 'device',
      header: '设备',
      render: (row) => <span className="text-fg-muted">{describeUserAgent(row.userAgent)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="登录历史"
        description="成功与失败的登录都会记录，最多保留最近 500 条在导出里。"
        action={
          // 同源下载，直接给链接即可，不需要前端拼 CSV。
          <a href="/api/security/login-history.csv" download>
            <Button icon="list">导出 CSV</Button>
          </a>
        }
      />

      <Card>
        <CardHeader title="登录记录" hint="按时间倒序" />
        <Table
          caption="登录历史"
          columns={columns}
          rows={history.data?.items ?? []}
          getKey={(row) => row.id}
          loading={history.loading}
          empty={{ title: '还没有登录记录' }}
        />
        <Pagination
          page={page}
          pageSize={history.data?.pageSize ?? 20}
          total={history.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>
    </div>
  );
}
